import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { withRouter, Link } from 'react-router-dom';
import {
  Segment, Header, Icon, Table, Message, Loader, Button, Input, Label, Tab, Form
} from 'semantic-ui-react';
import MultiplyHeader from '../components/MultiplyHeader/MultiplyHeader';
import { componentContainerStyle, componentContentStyle } from '../constants';

const SEVERITY_COLORS = {
  error: 'red',
  warning: 'yellow',
  info: 'blue'
};

const SEVERITY_ICONS = {
  error: 'times circle',
  warning: 'warning sign',
  info: 'info circle'
};

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const truncId = (id, len = 18) => {
  if (!id) return '-';
  return id.length > len ? `${id.substring(0, len)}...` : id;
};

const HealthPill = ({ flagCount }) => {
  const counts = flagCount || {};
  if (counts.error > 0) {
    return (
      <Label color='red' size='small'>
        <Icon name='times circle' /> {counts.error} error{counts.error > 1 ? 's' : ''}
      </Label>
    );
  }
  if (counts.warning > 0) {
    return (
      <Label color='yellow' size='small'>
        <Icon name='warning sign' /> {counts.warning} warning{counts.warning > 1 ? 's' : ''}
      </Label>
    );
  }
  if (counts.info > 0) {
    return (
      <Label color='blue' size='small'>
        <Icon name='info circle' /> {counts.info} info
      </Label>
    );
  }
  return (
    <Label color='green' size='small'>
      <Icon name='check circle' /> clean
    </Label>
  );
};

const BotRow = ({ bot }) => (
  <Table.Row>
    <Table.Cell>
      <Button as={Link} to={`/murphy/bot-inspector/${bot.botId}`} basic size='small' icon labelPosition='left'>
        <Icon name='search' /> Inspect
      </Button>
    </Table.Cell>
    <Table.Cell>{formatTime(bot.scheduledStartTime)}</Table.Cell>
    <Table.Cell>{bot.meetingTitle || '-'}</Table.Cell>
    <Table.Cell>{bot.status || '-'}{bot.stage ? <div style={{ fontSize: '0.8em', color: '#888' }}>stage: {bot.stage}</div> : null}</Table.Cell>
    <Table.Cell>{bot.assignedTo ? truncId(bot.assignedTo, 16) : <span style={{ color: '#999' }}>unassigned</span>}</Table.Cell>
    <Table.Cell>{bot.companyId ? truncId(bot.companyId, 16) : '-'}</Table.Cell>
    <Table.Cell><code style={{ fontSize: '0.85em' }}>{truncId(bot.botId)}</code></Table.Cell>
    <Table.Cell><HealthPill flagCount={bot.flagCount} /></Table.Cell>
  </Table.Row>
);

const BotTable = ({ bots, emptyText }) => (
  <Table celled striped compact>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
        <Table.HeaderCell width={2}>Scheduled</Table.HeaderCell>
        <Table.HeaderCell>Meeting</Table.HeaderCell>
        <Table.HeaderCell width={2}>Status</Table.HeaderCell>
        <Table.HeaderCell width={2}>Assigned To</Table.HeaderCell>
        <Table.HeaderCell width={2}>Company</Table.HeaderCell>
        <Table.HeaderCell width={2}>Bot ID</Table.HeaderCell>
        <Table.HeaderCell width={2}>Health</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {bots.length === 0 ? (
        <Table.Row>
          <Table.Cell colSpan='8' textAlign='center' style={{ color: '#999' }}>{emptyText}</Table.Cell>
        </Table.Row>
      ) : (
        bots.map(b => <BotRow key={b.botId} bot={b} />)
      )}
    </Table.Body>
  </Table>
);

const ListMode = observer(({ store, onSubmitSearch, searchValue, setSearchValue }) => {
  const { loading, error, upcoming, recent, generatedAt } = store.list;

  return (
    <>
      <Segment>
        <Header as='h3'><Icon name='search' /> Look up a specific bot</Header>
        <Form onSubmit={onSubmitSearch}>
          <Form.Field>
            <label>Internal bot ID (e.g. <code>bot-1779...</code>) or recall.ai UUID</label>
            <Input
              placeholder='bot-1779373723639-svoftmazj  OR  1e4c6de3-1aae-...'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              action={{ icon: 'arrow right', primary: true, type: 'submit' }}
            />
          </Form.Field>
        </Form>
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Failed to load list</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading && <Loader active inline='centered' content='Loading bots...' />}

      {!loading && (() => {
        const all = [...upcoming, ...recent];
        const errorCount = all.reduce((n, b) => n + (b.flagCount?.error || 0), 0);
        const warningCount = all.reduce((n, b) => n + (b.flagCount?.warning || 0), 0);
        const infoCount = all.reduce((n, b) => n + (b.flagCount?.info || 0), 0);
        const cleanCount = all.filter(b => !b.flagCount || (
          !b.flagCount.error && !b.flagCount.warning && !b.flagCount.info
        )).length;
        return (
          <Segment>
            <Header as='h4' style={{ margin: 0 }}>
              <Icon name='dashboard' />
              Summary: {all.length} bot{all.length !== 1 ? 's' : ''} in window
            </Header>
            <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Label color='green' size='small'><Icon name='check circle' /> {cleanCount} clean</Label>
              {errorCount > 0 && <Label color='red' size='small'><Icon name='times circle' /> {errorCount} error{errorCount !== 1 ? 's' : ''}</Label>}
              {warningCount > 0 && <Label color='yellow' size='small'><Icon name='warning sign' /> {warningCount} warning{warningCount !== 1 ? 's' : ''}</Label>}
              {infoCount > 0 && <Label color='blue' size='small'><Icon name='info circle' /> {infoCount} info</Label>}
            </div>
          </Segment>
        );
      })()}

      {!loading && (
        <>
          <Segment>
            <Header as='h3'>
              <Icon name='calendar plus' />
              Upcoming ({upcoming.length})
              <Header.Subheader>Bots scheduled to run in the future</Header.Subheader>
            </Header>
            <BotTable bots={upcoming} emptyText='No upcoming bots scheduled' />
          </Segment>

          <Segment>
            <Header as='h3'>
              <Icon name='history' />
              Last 7 days ({recent.length})
              <Header.Subheader>Bots whose meeting time falls within the past week</Header.Subheader>
            </Header>
            <BotTable bots={recent} emptyText='No bots in the last 7 days' />
          </Segment>

          {generatedAt && (
            <div style={{ textAlign: 'right', color: '#888', fontSize: '0.85em', marginTop: '8px' }}>
              Snapshot taken at {formatTime(generatedAt)}
            </div>
          )}
        </>
      )}
    </>
  );
});

const FlagPanel = ({ flags, store, botId }) => {
  if (!flags || flags.length === 0) {
    return (
      <Message positive>
        <Icon name='check circle' />
        No consistency issues detected.
      </Message>
    );
  }

  const active = flags.filter(f => !f.acknowledged);
  const acknowledged = flags.filter(f => f.acknowledged);

  const grouped = active.reduce((acc, f) => {
    (acc[f.severity] = acc[f.severity] || []).push(f);
    return acc;
  }, {});

  const onDismiss = (check) => () => store.acknowledgeFlag(botId, check, 'acknowledge');
  const onUndo    = (check) => () => store.acknowledgeFlag(botId, check, 'unacknowledge');

  return (
    <>
      {active.length === 0 && (
        <Message positive>
          <Icon name='check circle' />
          No active consistency issues. {acknowledged.length > 0 && `(${acknowledged.length} acknowledged below)`}
        </Message>
      )}

      {['error', 'warning', 'info'].map(sev => {
        const items = grouped[sev];
        if (!items || items.length === 0) return null;
        return (
          <Message key={sev} color={SEVERITY_COLORS[sev]}>
            <Message.Header>
              <Icon name={SEVERITY_ICONS[sev]} /> {sev.toUpperCase()} ({items.length})
            </Message.Header>
            <Message.List>
              {items.map((f, i) => (
                <Message.Item key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <strong>{f.check}:</strong> {f.message}
                    </div>
                    <button
                      onClick={onDismiss(f.check)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#0884a8',
                        cursor: 'pointer',
                        fontSize: '12px',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                  <pre style={{ background: 'rgba(0,0,0,0.04)', padding: '8px', marginTop: '4px', fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(f.evidence, null, 2)}
                  </pre>
                </Message.Item>
              ))}
            </Message.List>
          </Message>
        );
      })}

      {acknowledged.length > 0 && (
        <Message style={{ opacity: 0.65, marginTop: '8px' }}>
          <Message.Header style={{ fontSize: '13px' }}>
            <Icon name='archive' /> Acknowledged ({acknowledged.length})
          </Message.Header>
          <Message.List>
            {acknowledged.map((f, i) => (
              <Message.Item key={i} style={{ fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{f.check}</strong> ({f.severity}): {f.message}
                  </div>
                  <button
                    onClick={onUndo(f.check)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#0884a8',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textDecoration: 'underline',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Undo dismiss
                  </button>
                </div>
              </Message.Item>
            ))}
          </Message.List>
        </Message>
      )}
    </>
  );
};

const TimelineSection = ({ events }) => (
  <Segment>
    <Header as='h3'><Icon name='history' /> Timeline (from our DynamoDB tables)</Header>
    {(!events || events.length === 0) ? (
      <Message info>No timeline events found</Message>
    ) : (
      <div style={{ paddingLeft: '16px' }}>
        {events.map((step, i) => (
          <Segment key={i} style={{ marginBottom: '12px', borderLeft: '4px solid #2185d0' }}>
            <Header as='h5'>
              {step.event}
              <Header.Subheader>
                {formatTime(step.at)} &middot; <code style={{ fontSize: '11px' }}>{step.source}</code>
              </Header.Subheader>
            </Header>
            {step.details && Object.keys(step.details).length > 0 && (
              <Table basic='very' compact='very' style={{ marginTop: '6px' }}>
                <Table.Body>
                  {Object.entries(step.details).map(([k, v]) => (
                    <Table.Row key={k}>
                      <Table.Cell width={4} style={{ fontWeight: 'bold', color: '#666' }}>{k}</Table.Cell>
                      <Table.Cell>
                        {typeof v === 'object' ? <code style={{ fontSize: '12px' }}>{JSON.stringify(v)}</code> : String(v ?? '-')}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Segment>
        ))}
      </div>
    )}
  </Segment>
);

const RawRecordTab = ({ record, label }) => {
  if (!record) {
    return <Message info>No {label} record found.</Message>;
  }
  return (
    <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', overflow: 'auto', fontSize: '12px', maxHeight: '600px' }}>
      {JSON.stringify(record, null, 2)}
    </pre>
  );
};

const RawRecordsList = ({ records, label }) => {
  if (!records || records.length === 0) {
    return <Message info>No {label} records found.</Message>;
  }
  return (
    <>
      {records.map((r, i) => (
        <Segment key={r.id || i}>
          <Header as='h5'>
            {label} #{i + 1}{r.id ? <span style={{ fontFamily: 'monospace', fontWeight: 'normal', marginLeft: '8px', fontSize: '0.9em' }}>{r.id}</span> : null}
          </Header>
          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', overflow: 'auto', fontSize: '12px', maxHeight: '500px' }}>
            {JSON.stringify(r, null, 2)}
          </pre>
        </Segment>
      ))}
    </>
  );
};

const DetailMode = observer(({ store, botId }) => {
  const { loading, error, result } = store.detail;

  useEffect(() => {
    if (botId) {
      store.loadBot(botId);
    }
    return () => store.clearDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  if (loading) {
    return <Loader active inline='centered' content={`Loading ${botId}...`} />;
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Failed to load bot</Message.Header>
        <p>{error}</p>
        <Button as={Link} to='/murphy/bot-inspector' size='small'>← Back to list</Button>
      </Message>
    );
  }

  if (!result) return null;

  const { resolved, bot, assessments, assignedToUser, company, interactionContexts, ourTimeline, consistencyFlags, fetchErrors, interactionContextsTruncated } = result;

  const panes = [
    { menuItem: 'Bot record', render: () => <Tab.Pane><RawRecordTab record={bot} label='bot' /></Tab.Pane> },
    { menuItem: `Assessments (${(assessments || []).length})`, render: () => <Tab.Pane><RawRecordsList records={assessments} label='assessment' /></Tab.Pane> },
    { menuItem: 'Assigned-to user', render: () => <Tab.Pane><RawRecordTab record={assignedToUser} label='user' /></Tab.Pane> },
    { menuItem: 'Company', render: () => <Tab.Pane><RawRecordTab record={company} label='company' /></Tab.Pane> },
    { menuItem: `Interaction context (${(interactionContexts || []).length})`, render: () => <Tab.Pane><RawRecordsList records={interactionContexts} label='interaction-context' /></Tab.Pane> }
  ];

  return (
    <>
      <Segment>
        <Button as={Link} to='/murphy/bot-inspector' basic size='small' icon labelPosition='left'>
          <Icon name='arrow left' />
          Back to list
        </Button>
        <Header as='h3' style={{ marginTop: '12px' }}>
          <Icon name='id badge' />
          {resolved.botId}
          <Header.Subheader>
            Resolved via <strong>{resolved.inputType}</strong> id
            {resolved.recallBotId ? <> &middot; recall.ai: <code style={{ fontSize: '12px' }}>{resolved.recallBotId}</code></> : null}
          </Header.Subheader>
        </Header>

        <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {bot.meetingUrl && (
            <Button as='a' href={bot.meetingUrl} target='_blank' rel='noopener noreferrer' size='small' basic icon labelPosition='left'>
              <Icon name='external' />
              Open meeting URL
            </Button>
          )}
          {resolved.recallBotId && (
            <Button
              as='a'
              href={`https://us-west-2.recall.ai/dashboard/bot/${resolved.recallBotId}`}
              target='_blank'
              rel='noopener noreferrer'
              size='small'
              basic
              icon
              labelPosition='left'
            >
              <Icon name='external' />
              Open in recall.ai
            </Button>
          )}
          {bot.calendarEventId && (
            <Label size='small'>calendarEventId: <code>{truncId(bot.calendarEventId, 24)}</code></Label>
          )}
        </div>
      </Segment>

      {fetchErrors && fetchErrors.length > 0 && (
        <Message warning>
          <Message.Header>
            <Icon name='exclamation triangle' /> Partial data — some related records failed to load
          </Message.Header>
          <Message.List>
            {fetchErrors.map((e, i) => (
              <Message.Item key={i}>
                <strong>{e.source}:</strong> {e.message}
              </Message.Item>
            ))}
          </Message.List>
          <p style={{ marginTop: '8px' }}>The records above may be incomplete. Reload to retry.</p>
        </Message>
      )}

      <Segment>
        <Header as='h3'><Icon name='clipboard check' /> Consistency checks</Header>
        <FlagPanel flags={consistencyFlags} store={store} botId={resolved.botId} />
      </Segment>

      <TimelineSection events={ourTimeline} />

      <Segment>
        <Header as='h3'><Icon name='database' /> Raw records by table</Header>
        {interactionContextsTruncated && (
          <Message info size='tiny' style={{ marginBottom: '12px' }}>
            <Icon name='info circle' /> Interaction-context fetch truncated at 10 rows.
            More referenced by older assessments are not shown.
          </Message>
        )}
        <Tab panes={panes} />
      </Segment>
    </>
  );
});

const BotInspectorPage = observer(({ match, history, botInspectorStore }) => {
  const botId = match?.params?.botId || null;
  const store = botInspectorStore;
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!botId) {
      store.loadList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    // Strip whitespace and surrounding quotes — common when pasting from
    // Slack/CLI. Backend further lowercases recall UUIDs (case-sensitive GSI).
    const v = (searchValue || '').trim().replace(/^["']+|["']+$/g, '');
    if (!v) return;
    history.push(`/murphy/bot-inspector/${encodeURIComponent(v)}`);
  };

  return (
    <div style={componentContainerStyle}>
      <MultiplyHeader header={botId ? `Bot Inspector — ${botId}` : 'Bot Inspector'} />
      <div style={componentContentStyle}>
        {botId
          ? <DetailMode store={store} botId={botId} />
          : <ListMode store={store} onSubmitSearch={onSubmitSearch} searchValue={searchValue} setSearchValue={setSearchValue} />
        }
      </div>
    </div>
  );
});

export default withRouter(BotInspectorPage);
